"use client";

import { Card, CardBody } from "@heroui/card";
import { FaArrowRight, FaExternalLinkAlt } from "react-icons/fa";

import { card } from "@/components/primitives";
import { Link } from "@/i18n/navigation";

interface ExploreMoreItem {
  title: string;
  description: string;
  href: string;
  external?: boolean;
  category?: string;
}

interface ExploreMoreClientProps {
  items: ExploreMoreItem[];
  variant?: "grid" | "list" | "inline";
  showDescription?: boolean;
  showCategory?: boolean;
}

export function ExploreMoreClient({
  items,
  variant = "grid",
  showDescription = true,
  showCategory = false,
}: ExploreMoreClientProps) {
  const renderItem = (item: ExploreMoreItem, index: number) => {
    const content = (
      <>
        <div className="flex items-center justify-between">
          <p className="text-lg font-bold text-foreground group-hover:text-primary transition-colors">
            {item.title}
          </p>
          {item.external ? (
            <FaExternalLinkAlt className="h-5 w-5 text-default-400 group-hover:text-primary transition-colors" />
          ) : (
            <FaArrowRight className="h-5 w-5 text-default-400 group-hover:text-primary group-hover:translate-x-1 transition-all" />
          )}
        </div>
        {showCategory && item.category && (
          <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded-full mt-2 inline-block">
            {item.category}
          </span>
        )}
        {showDescription && (
          <p className="text-base text-default-600 mt-3 group-hover:text-default-700 transition-colors">
            {item.description}
          </p>
        )}
      </>
    );

    if (item.external) {
      return (
        <Card
          key={index}
          isPressable
          as="a"
          className={`${card({ variant: "solid", size: "lg", bordered: false, elevation: "md" })} group cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-[1.02]`}
          href={item.href}
          rel="noopener noreferrer"
          target="_blank"
        >
          <CardBody>{content}</CardBody>
        </Card>
      );
    }

    return (
      <Card
        key={index}
        isPressable
        as={Link}
        className={`${card({ variant: "solid", size: "lg", bordered: false, elevation: "md" })} group cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-[1.02]`}
        href={item.href}
      >
        <CardBody>{content}</CardBody>
      </Card>
    );
  };

  const getVariantClasses = (): string => {
    if (variant === "list") {
      return "flex flex-col gap-3";
    }
    if (variant === "inline") {
      return "flex flex-wrap gap-2";
    }

    // Grid variant (default)
    // Show 1 item on sm, max 2 items on md, max 3 items on lg
    // Single item expands to full width, 2 items each take half width
    if (items.length === 1) {
      return "grid grid-cols-1 gap-4";
    }
    if (items.length === 2) {
      return "grid grid-cols-1 md:grid-cols-2 gap-4";
    }

    return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4";
  };

  return (
    <div className={getVariantClasses()}>
      {items.map((item, index) => renderItem(item, index))}
    </div>
  );
}
